import React from 'react';
import { ArrowDown, Server, Monitor, Clock } from 'lucide-react';
import useInView from '../hooks/useInView';

const PAIRS = [
  {
    icon: Server,
    painGradient: 'kpi-card--iris',
    gradient: 'kpi-card--ocean',
    pain: 'Expensive servers & hardware',
    painSub: 'EPABX setup costs lakhs. IT team required before day one.',
    solution: 'Zero infrastructure needed',
    solutionSub: 'Works on any Android phone your agents already own.',
  },
  {
    icon: Monitor,
    painGradient: 'kpi-card--coral',
    gradient: 'kpi-card--violet',
    pain: 'Agents tied to a desk',
    painSub: 'Your team can only call from the office.',
    solution: 'Call from anywhere, anytime',
    solutionSub: 'Remote-first. Field agents. Work-from-home. All covered.',
  },
  {
    icon: Clock,
    painGradient: 'kpi-card--citrus',
    gradient: 'kpi-card--mint',
    pain: 'Setup takes weeks',
    painSub: 'Vendors, wiring, licenses, training — before one call is made.',
    solution: 'Live in under 30 minutes',
    solutionSub: 'Sign up, add agents, upload leads, start dialing. That’s it.',
  },
];

const PainSolutionSection = () => {
  const [ref, inView] = useInView();

  return (
  <section ref={ref} className={`section section--muted reveal ${inView ? 'is-inview' : ''}`}>
    <div className="container-page">
      <div className="section-head">
        <span className="eyebrow accent-2">The problem</span>
        <h2 className="section-title">
          The old way is <span className="display-accent">broken</span>
        </h2>
        <p className="section-sub">
          Traditional call center infrastructure was built for a different era. WASLX was built for
          right now.
        </p>
      </div>

      {/* Three rows across all columns, so the arrows and the WASLX cards
          line up even when the pain copy runs to different lengths. */}
      <div className="stagger-children grid gap-x-6 gap-y-3 md:grid-cols-3 md:grid-rows-[auto_auto_1fr]">
        {PAIRS.map((pair, i) => {
          const Icon = pair.icon;
          const col = { gridColumn: i + 1 };
          return (
            <React.Fragment key={pair.pain}>
              {/* The old way, on the same KPI treatment as the row below it.
                  The before/after no longer comes from one row being pale and
                  the other saturated — both are saturated, and the contrast
                  is carried by hue TEMPERATURE instead: this row runs warm
                  (red, orange, citrus), the WASLX row runs cool (blue, violet,
                  mint). Same reading, equal visual weight. */}
              <div
                className={`${pair.painGradient} kpi-card card--hover reveal-color md:row-start-1`}
                style={{ ...col, '--reveal-delay': `${i * 90}ms` }}
              >
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="icon-tile icon-tile--sm">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-2xs font-bold uppercase tracking-[0.1em] text-white/80">
                    Old way
                  </span>
                </div>
                <p className="text-[0.9375rem] font-bold text-white">{pair.pain}</p>
                <p className="mt-1 text-sm leading-relaxed text-white/85">{pair.painSub}</p>
              </div>

              <div className="flex justify-center md:row-start-2" style={col}>
                <span className="flex h-8 w-8 items-center justify-center rounded-pill bg-surface text-primary-strong shadow-card">
                  <ArrowDown className="h-4 w-4" />
                </span>
              </div>

              {/* The dashboard KPI treatment, not .card--vivid: that one is
                  built on --gradient-nav, so every card came out the same
                  navy with only a hint of its accent at the top edge. These
                  are the saturated one-hue-per-card gradients the product's
                  KPI row uses. */}
              <div
                className={`${pair.gradient} kpi-card card--hover reveal-color md:row-start-3`}
                style={{ ...col, '--reveal-delay': `${i * 90 + 160}ms` }}
              >
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="icon-tile icon-tile--sm">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-2xs font-bold uppercase tracking-[0.1em] text-white/80">
                    WASLX
                  </span>
                </div>
                <p className="text-[0.9375rem] font-bold text-white">{pair.solution}</p>
                <p className="mt-1 text-sm leading-relaxed text-white/85">{pair.solutionSub}</p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </section>
  );
};

export default PainSolutionSection;
